import { Injectable } from "@angular/core";
import { addDoc, collection, deleteDoc, docData, Firestore } from "@angular/fire/firestore";
import { filter, firstValueFrom, map } from "rxjs";
import AppLauncherElectron from "src/app/plugins/app-launcher.plugin";
import { environment } from "src/environments/environment";
import { isElectron } from "../../utils/platform.utils";

// Detect Electron via the exposed CapacitorCustomPlatform from preload script
declare global {
    interface Window {
        CapacitorCustomPlatform?: {
            name: string;
            plugins: Record<string, any>;
        };
        electronAPI?: {
            onDeepLink: (callback: (...args: unknown[]) => void) => void;
            offDeepLink: (callback: (...args: unknown[]) => void) => void;
            onAutoUpdateStatus: (callback: (...args: unknown[]) => void) => void;
            offAutoUpdateStatus: (callback: (...args: unknown[]) => void) => void;
        };
        electron: {
            ipcRenderer: {
                invoke(channel: string, ...args: any[]): Promise<any>;
                send(channel: string, ...args: any[]): void;
                on(channel: string, listener: (...args: any[]) => void): void;
                removeListener(channel: string, listener: (...args: any[]) => void): void;
            };
        };
        __gameTabName?: string;
    }
}

@Injectable({
    providedIn: 'root',
})
export class ElectronService {

    constructor(
        private firestore: Firestore,
    ) { }

    isElectronApp(): boolean {
        return isElectron();
    }

    getDeeplinkUrl(): string {
        return environment.staging ? 'sopo-desktop-staging://' : 'sopo-desktop://';
    }

    async externalLogin(provider: Provider): Promise<string> {
        const collectionRef = collection(this.firestore, 'externalLoginAttempts');
        const loginOptions: {
            provider: Provider
        } = {provider}
        const docRef = await addDoc(collectionRef, loginOptions);
        const appUrl = environment.staging ? 'https://web.staging.playsopo.com' : 'https://app.playsopo.com';
        await AppLauncherElectron.openUrl({url: `${appUrl}/desktop-login?attemptId=${docRef.id}`});
        const resultObs = docData(docRef).pipe(
            map(doc => doc as IDesktopLoginAttempt),
            filter(data => data.response !== undefined),
            map(data => data.response!)
        );
        const result = await firstValueFrom(resultObs);

        await deleteDoc(docRef);

        if (result.success === false) {
            throw new Error(result.reason);
        }
        return result.credentialToken;
    }

    public async externalPhantomConnect(): Promise<string> {
        try {
            const collectionRef = collection(this.firestore, 'externalPhantomConnect');
            const docRef = await addDoc(collectionRef, {});
    
            const appUrl = environment.staging ? 'https://sopo-staging-web.firebaseapp.com' : 'https://app.playsopo.com';
            await AppLauncherElectron.openUrl({url: `${appUrl}/phantom-connect?attemptId=${docRef.id}`});
    
            const resultObs = docData(docRef).pipe(
                map(doc => doc as IPhantomConnectAttempt),
                filter(data => data.response !== undefined),
                map(data => data.response!)
            );
            const result = await firstValueFrom(resultObs);
    
            await deleteDoc(docRef);
    
            if (result.success === false) {
                throw result;
            }
            return result.address;
        } catch (error) {
            if (error && error.reason) throw new Error(error.reason);
            else {
                console.error('[ElectronService] - externalPhantomConnect', error);
                throw new Error('Something went wrong.');
            }
        }
    }
}

type Provider = 'google' | 'apple' | 'facebook';

export interface IDesktopLoginAttempt {
    provider: Provider, 
    response?: {
        success: true, 
        credentialToken: string
    } | {
        success: false, 
        reason: string
    }
}

export interface IPhantomConnectAttempt {
    response?: {
        success: true, 
        address: string
    } | {
        success: false, 
        reason: string
    }
}
